import { useState, useEffect, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Loader2, Plus, Pencil, Trash2, Users, UserCheck, Star, UserPlus, AlertCircle, DollarSign, Receipt, ChevronDown, ChevronUp, ContactRound, Upload, X, History } from "lucide-react";
import { formatCurrency } from "@/lib/currency";
import { readJsonArray } from "@/lib/http";
import { format } from "date-fns";
import * as XLSX from "xlsx";
import { mapContactRows, parseVCardContacts, type ImportedContact } from "@/lib/contact-import";
import { CustomerHistoryDialog } from "@/components/customer-history-dialog";

const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");
const QK = ["customers"];

const EMPTY_FORM = { name: "", email: "", phone: "", address: "", notes: "", discountRate: "" };

// ─── Customer Form Dialog ─────────────────────────────────────────────────────
function CustomerFormDialog({ open, onClose, editId, form, setForm, onSubmit, isPending, onImport, importPending }: {
  open: boolean; onClose: () => void; editId: number | null;
  form: typeof EMPTY_FORM; setForm: (f: typeof EMPTY_FORM) => void;
  onSubmit: (e: React.FormEvent) => void; isPending: boolean;
  onImport: (contacts: ImportedContact[]) => void; importPending: boolean;
}) {
  const [formError, setFormError] = useState("");
  const [contacts, setContacts] = useState<ImportedContact[]>([]);
  const fileRef = useRef<HTMLInputElement>(null);
  const contactPickerSupported = typeof navigator !== "undefined" && "contacts" in navigator;
  useEffect(() => { if (!open) setContacts([]); }, [open]);
  const chooseDeviceContacts = async () => {
    try {
      const selected = await (navigator as any).contacts.select(["name", "tel", "email"], { multiple: true });
      setContacts(selected.map((contact: any) => ({
        name: String(contact.name?.[0] ?? ""),
        phone: String(contact.tel?.[0] ?? ""),
        email: String(contact.email?.[0] ?? ""),
      })).filter((contact: ImportedContact) => contact.name && (contact.phone || contact.email)));
    } catch (error: any) {
      if (error?.name !== "AbortError") setFormError("Could not open your contacts. Upload a VCF or CSV file instead.");
    }
  };
  const loadContactFile = async (file?: File) => {
    if (!file) return;
    try {
      let imported: ImportedContact[];
      if (file.name.toLowerCase().endsWith(".vcf")) {
        imported = parseVCardContacts(await file.text());
      } else {
        const workbook = XLSX.read(await file.arrayBuffer());
        imported = mapContactRows(XLSX.utils.sheet_to_json<Record<string, unknown>>(workbook.Sheets[workbook.SheetNames[0]], { defval: "" }));
      }
      setContacts(imported);
      if (!imported.length) setFormError("No contacts with a name and phone number or email were found.");
    } catch {
      setFormError("That contacts file could not be read. Please use a VCF or CSV file.");
    }
  };
  const handleSubmit = (e: React.FormEvent) => {
    setFormError("");
    if (!form.name.trim()) { e.preventDefault(); setFormError("Name is required"); return; }
    onSubmit(e);
  };
  return (
    <Dialog open={open} onOpenChange={o => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{editId ? "Edit Customer" : "Add Customer"}</DialogTitle></DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          {!editId && (
            <div className="rounded-xl border bg-muted/30 p-3 space-y-3">
              <div>
                <p className="text-sm font-semibold">Add from your contacts</p>
                <p className="text-xs text-muted-foreground">You choose exactly which contacts RetailPulsePOS can add.</p>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Button type="button" variant="outline" className="gap-2" disabled={!contactPickerSupported} onClick={chooseDeviceContacts}>
                  <ContactRound className="w-4 h-4" /> Phone contacts
                </Button>
                <Button type="button" variant="outline" className="gap-2" onClick={() => fileRef.current?.click()}>
                  <Upload className="w-4 h-4" /> VCF or CSV
                </Button>
                <input ref={fileRef} hidden type="file" accept=".vcf,.csv,text/vcard,text/csv" onChange={e => { loadContactFile(e.target.files?.[0]); e.target.value = ""; }} />
              </div>
              {!contactPickerSupported && <p className="text-xs text-muted-foreground">Direct selection works on supported Android browsers. iPhone, iPad and laptops can upload an exported VCF or CSV file.</p>}
              {contacts.length > 0 && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-medium">{contacts.length} contact{contacts.length === 1 ? "" : "s"} ready to review</p>
                    <Button type="button" size="icon" variant="ghost" className="h-7 w-7" onClick={() => setContacts([])}><X className="w-4 h-4" /></Button>
                  </div>
                  <div className="max-h-36 overflow-y-auto rounded-lg border bg-background divide-y">
                    {contacts.map((contact, index) => (
                      <div key={`${contact.phone}-${index}`} className="px-3 py-2">
                        <p className="text-sm font-medium">{contact.name}</p>
                        <p className="text-xs text-muted-foreground">{contact.phone || contact.email}</p>
                      </div>
                    ))}
                  </div>
                  <Button type="button" className="w-full gap-2" disabled={importPending} onClick={() => onImport(contacts)}>
                    {importPending && <Loader2 className="w-4 h-4 animate-spin" />}
                    Add {contacts.length} contact{contacts.length === 1 ? "" : "s"} to this store
                  </Button>
                </div>
              )}
            </div>
          )}
          <div>
            <label className="text-sm font-medium">Full Name</label>
            <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="mt-1" placeholder="e.g. Chidi Okonkwo" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium">Phone</label>
              <Input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} className="mt-1" placeholder="080..." />
            </div>
            <div>
              <label className="text-sm font-medium">Email</label>
              <Input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} className="mt-1" placeholder="optional" />
            </div>
            <div>
              <label className="text-sm font-medium">Discount Rate (%)</label>
              <Input type="number" step="0.5" min="0" max="100" value={form.discountRate} onChange={e => setForm({ ...form, discountRate: e.target.value })} className="mt-1" placeholder="0" />
            </div>
          </div>
          <div>
            <label className="text-sm font-medium">Address</label>
            <Input value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} className="mt-1" placeholder="optional" />
          </div>
          <div>
            <label className="text-sm font-medium">Notes</label>
            <Input value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} className="mt-1" placeholder="optional" />
          </div>
          {formError && <p className="text-sm text-destructive bg-destructive/10 px-3 py-2 rounded">{formError}</p>}
          <Button type="submit" className="w-full" disabled={isPending}>
            {isPending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            {editId ? "Save Changes" : "Add Customer"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ─── Debt / Credit Balances Tab ───────────────────────────────────────────────
function DebtTab({ onViewHistory }: { onViewHistory: (id: number) => void }) {
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [payingId, setPayingId] = useState<number | null>(null);
  const [payAmount, setPayAmount] = useState("");
  const [paying, setPaying] = useState(false);
  const paymentReference = useRef<string | null>(null);
  const qc = useQueryClient();
  const { toast } = useToast();

  const { data: debts, isLoading, refetch } = useQuery<any[]>({
    queryKey: ["customers-unpaid"],
    queryFn: async () => {
      const res = await fetch(`${BASE}/api/customers/unpaid`, { credentials: "include" });
      return readJsonArray<any>(res, "customer balances");
    },
  });

  const totalOutstanding = debts?.reduce((s, d) => s + d.totalBalance, 0) ?? 0;
  const totalDebtors = debts?.length ?? 0;

  const recordPayment = async (saleId: number, maxAmount: number) => {
    const amount = Number(payAmount);
    if (!amount || amount <= 0 || amount > maxAmount) return;
    setPaying(true);
    const clientReference = paymentReference.current ?? crypto.randomUUID();
    paymentReference.current = clientReference;
    try {
      const res = await fetch(`${BASE}/api/sales/${saleId}/payment`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ amount, clientReference }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        paymentReference.current = null;
        throw new Error(body.error || "Could not record payment");
      }
      paymentReference.current = null;
      setPayingId(null);
      setPayAmount("");
      refetch();
      qc.invalidateQueries({ queryKey: QK });
      qc.invalidateQueries({ queryKey: ["customer-history"] });
    } catch (error) {
      toast({ title: error instanceof Error ? error.message : "Could not record payment", variant: "destructive" });
    } finally { setPaying(false); }
  };

  if (isLoading) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;

  if (!debts?.length) return (
    <div className="flex flex-col items-center justify-center py-20 gap-3 text-center">
      <div className="w-14 h-14 rounded-2xl bg-emerald-50 flex items-center justify-center">
        <DollarSign className="w-7 h-7 text-emerald-500" />
      </div>
      <div>
        <p className="font-semibold text-lg">No outstanding debts 🎉</p>
        <p className="text-sm text-muted-foreground mt-1">All credit sales have been fully paid.</p>
      </div>
    </div>
  );

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
        <Card className="border-l-4 border-l-red-500">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between mb-1">
              <span className="text-sm text-muted-foreground">Total Outstanding</span>
              <AlertCircle className="w-4 h-4 text-red-500" />
            </div>
            <div className="text-2xl font-bold text-red-600">{formatCurrency(totalOutstanding)}</div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-orange-400">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between mb-1">
              <span className="text-sm text-muted-foreground">Customers in Debt</span>
              <Users className="w-4 h-4 text-orange-400" />
            </div>
            <div className="text-2xl font-bold">{totalDebtors}</div>
          </CardContent>
        </Card>
      </div>

      <div className="space-y-3">
        {debts.map((debtor: any) => (
          <Card key={debtor.customerId}>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-base">{debtor.customerName}</CardTitle>
                  {debtor.customerPhone && <p className="text-xs text-muted-foreground">{debtor.customerPhone}</p>}
                  <Button variant="link" size="sm" className="h-auto p-0 text-xs" onClick={() => onViewHistory(debtor.customerId)} data-testid={`button-debt-history-${debtor.customerId}`}>
                    View history
                  </Button>
                </div>
                <div className="text-right">
                  <p className="text-lg font-bold text-red-600">{formatCurrency(debtor.totalBalance)}</p>
                  <p className="text-xs text-muted-foreground">{debtor.sales.length} unpaid sale{debtor.sales.length !== 1 ? "s" : ""}</p>
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                className="w-full mt-1 gap-1 text-xs h-7"
                onClick={() => setExpandedId(expandedId === debtor.customerId ? null : debtor.customerId)}
              >
                {expandedId === debtor.customerId ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                {expandedId === debtor.customerId ? "Hide" : "Show"} sales
              </Button>
            </CardHeader>
            {expandedId === debtor.customerId && (
              <CardContent className="pt-0">
                <div className="space-y-2">
                  {debtor.sales.map((sale: any) => (
                    <div key={sale.id} className="border rounded-lg p-3 space-y-2">
                      <div className="flex items-center justify-between text-sm">
                        <div className="flex items-center gap-2">
                          <Receipt className="w-3.5 h-3.5 text-muted-foreground" />
                          <span className="font-mono font-medium">{sale.receiptNumber}</span>
                          <span className="text-muted-foreground">{format(new Date(sale.date), "MMM d, yyyy")}</span>
                        </div>
                        <div className="text-right">
                          <p className="font-semibold text-red-600">{formatCurrency(sale.balance)} owed</p>
                          <p className="text-xs text-muted-foreground">{formatCurrency(sale.amountPaid)} of {formatCurrency(sale.totalAmount)} paid</p>
                        </div>
                      </div>
                      {payingId === sale.id ? (
                        <div className="flex gap-2">
                          <Input
                            type="number"
                            min="0.01"
                            max={sale.balance}
                            step="0.01"
                            placeholder={`Amount (max ${formatCurrency(sale.balance)})`}
                            value={payAmount}
                            onChange={e => { setPayAmount(e.target.value); paymentReference.current = null; }}
                            className="h-8 text-sm"
                            autoFocus
                          />
                          <Button size="sm" className="h-8 px-3 shrink-0" onClick={() => recordPayment(sale.id, sale.balance)} disabled={paying}>
                            {paying ? <Loader2 className="w-3 h-3 animate-spin" /> : "Pay"}
                          </Button>
                          <Button size="sm" variant="ghost" className="h-8 px-3 shrink-0" onClick={() => { setPayingId(null); setPayAmount(""); paymentReference.current = null; }}>Cancel</Button>
                        </div>
                      ) : (
                        <Button size="sm" variant="outline" className="w-full h-8 text-xs" onClick={() => { setPayingId(sale.id); setPayAmount(""); paymentReference.current = null; }}>
                          Record Payment
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            )}
          </Card>
        ))}
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function Customers() {
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);
  const [historyCustomerId, setHistoryCustomerId] = useState<number | null>(null);
  const [form, setForm] = useState(EMPTY_FORM);

  useEffect(() => {
    if (window.location.search.includes("add=1")) setIsOpen(true);
  }, []);

  const { data: customers, isLoading } = useQuery({
    queryKey: [...QK, search],
    queryFn: async () => {
      const p = search ? `?search=${encodeURIComponent(search)}` : "";
      const res = await fetch(`${BASE}/api/customers${p}`, { credentials: "include" });
      return readJsonArray<any>(res, "customers");
    },
  });

  const { toast } = useToast();

  const upsert = useMutation({
    mutationFn: async (data: typeof form) => {
      const url = editId ? `${BASE}/api/customers/${editId}` : `${BASE}/api/customers`;
      const method = editId ? "PATCH" : "POST";
      const res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ ...data, discountRate: Number(data.discountRate || 0) }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error((body as any).error || "Failed to save customer");
      }
      return res.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK });
      setIsOpen(false); setEditId(null); setForm(EMPTY_FORM);
    },
    onError: (e: Error) => toast({ title: e.message, variant: "destructive" }),
  });

  const del = useMutation({
    mutationFn: async (id: number) => {
      const res = await fetch(`${BASE}/api/customers/${id}`, { method: "DELETE", credentials: "include" });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error((body as any).error || "Failed to delete customer");
      }
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: QK }),
    onError: (e: Error) => toast({ title: e.message, variant: "destructive" }),
  });

  const importContacts = useMutation({
    mutationFn: async (contacts: ImportedContact[]) => {
      const res = await fetch(`${BASE}/api/customers/import`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ contacts }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || "Failed to import contacts");
      return body as { imported: number; skipped: number };
    },
    onSuccess: result => {
      qc.invalidateQueries({ queryKey: QK });
      setIsOpen(false);
      toast({ title: `${result.imported} customer${result.imported === 1 ? "" : "s"} added`, description: result.skipped ? `${result.skipped} duplicate or incomplete contact${result.skipped === 1 ? " was" : "s were"} skipped.` : undefined });
    },
    onError: (e: Error) => toast({ title: e.message, variant: "destructive" }),
  });

  const openEdit = (c: any) => {
    setForm({ name: c.name, email: c.email ?? "", phone: c.phone ?? "", address: c.address ?? "", notes: c.notes ?? "", discountRate: String(c.discountRate) });
    setEditId(c.id);
    setIsOpen(true);
  };

  const total = customers?.length ?? 0;
  const withDiscount = customers?.filter((c: any) => c.discountRate > 0).length ?? 0;
  const activeThisMonth = customers?.filter((c: any) => {
    if (!c.lastPurchaseAt) return false;
    const d = new Date(c.lastPurchaseAt);
    const now = new Date();
    return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
  }).length ?? 0;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Customers</h1>
          <p className="text-muted-foreground mt-1">Manage your customer base and track credit balances.</p>
        </div>
        <Button onClick={() => { setEditId(null); setForm(EMPTY_FORM); setIsOpen(true); }} className="gap-2">
          <Plus className="w-4 h-4" /> Add Customer
        </Button>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <Card className="border-l-4 border-l-blue-500">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between mb-1"><span className="text-sm text-muted-foreground">Total Customers</span><Users className="w-4 h-4 text-blue-500" /></div>
            <div className="text-2xl font-bold">{total}</div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-emerald-500">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between mb-1"><span className="text-sm text-muted-foreground">With Discount</span><Star className="w-4 h-4 text-emerald-500" /></div>
            <div className="text-2xl font-bold">{withDiscount}</div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-purple-500">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between mb-1"><span className="text-sm text-muted-foreground">Active This Month</span><UserCheck className="w-4 h-4 text-purple-500" /></div>
            <div className="text-2xl font-bold">{activeThisMonth}</div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="all">
        <TabsList>
          <TabsTrigger value="all" className="gap-1.5"><Users className="w-3.5 h-3.5" />All Customers</TabsTrigger>
          <TabsTrigger value="debts" className="gap-1.5"><AlertCircle className="w-3.5 h-3.5" />Credit Balances</TabsTrigger>
        </TabsList>

        <TabsContent value="all" className="mt-4 space-y-4">
          <div className="flex items-center gap-3">
            <Input placeholder="Search customers..." value={search} onChange={e => setSearch(e.target.value)} className="max-w-xs" />
            {search && <Button variant="ghost" size="sm" onClick={() => setSearch("")}>Clear</Button>}
          </div>

          <div className="border rounded-md overflow-hidden">
            <div className="overflow-y-auto max-h-[600px]">
            <Table>
              <TableHeader className="sticky top-0 bg-background z-10">
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Phone</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Discount</TableHead>
                  <TableHead>Loyalty Pts</TableHead>
                  <TableHead className="w-[140px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow><TableCell colSpan={6} className="text-center py-8"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></TableCell></TableRow>
                ) : !customers?.length ? (
                  <TableRow>
                    <TableCell colSpan={6} className="py-16">
                      <div className="flex flex-col items-center gap-3 text-center">
                        <div className="w-12 h-12 rounded-xl bg-muted flex items-center justify-center"><Users className="w-6 h-6 text-muted-foreground/50" /></div>
                        <div>
                          <p className="font-semibold">No customers yet</p>
                          <p className="text-sm text-muted-foreground mt-0.5">Add customers to track credit sales, loyalty points, and send them SMS.</p>
                        </div>
                        <Button size="sm" className="gap-2 mt-1" onClick={() => { setEditId(null); setForm(EMPTY_FORM); setIsOpen(true); }}>
                          <UserPlus className="w-4 h-4" /> Add First Customer
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : customers.map((c: any) => (
                  <TableRow key={c.id}>
                    <TableCell className="font-medium">{c.name}</TableCell>
                    <TableCell className="text-muted-foreground">{c.phone || "—"}</TableCell>
                    <TableCell className="text-muted-foreground text-sm">{c.email || "—"}</TableCell>
                    <TableCell>
                      {c.discountRate > 0
                        ? <Badge variant="secondary" className="text-emerald-700 bg-emerald-50">{c.discountRate}% off</Badge>
                        : <span className="text-muted-foreground">—</span>}
                    </TableCell>
                    <TableCell>
                      {c.loyaltyPoints > 0
                        ? <Badge variant="outline"><Star className="w-3 h-3 mr-1 inline" />{c.loyaltyPoints} pts</Badge>
                        : <span className="text-muted-foreground">—</span>}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Button variant="ghost" size="icon" className="h-8 w-8" title={`View ${c.name}'s history`} aria-label={`View ${c.name}'s history`} onClick={() => setHistoryCustomerId(c.id)} data-testid={`button-customer-history-${c.id}`}><History className="w-3.5 h-3.5" /></Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(c)}><Pencil className="w-3.5 h-3.5" /></Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => del.mutate(c.id)}><Trash2 className="w-3.5 h-3.5" /></Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="debts" className="mt-4">
          <DebtTab onViewHistory={setHistoryCustomerId} />
        </TabsContent>
      </Tabs>

      <CustomerFormDialog
        open={isOpen}
        onClose={() => { setIsOpen(false); setEditId(null); setForm(EMPTY_FORM); }}
        editId={editId}
        form={form}
        setForm={setForm}
        onSubmit={e => { e.preventDefault(); upsert.mutate(form); }}
        isPending={upsert.isPending}
        onImport={contacts => importContacts.mutate(contacts)}
        importPending={importContacts.isPending}
      />
      <CustomerHistoryDialog customerId={historyCustomerId} onClose={() => setHistoryCustomerId(null)} />
    </div>
  );
}
